import {
  Column,
  ColumnDef,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import {
  getPriceStatus,
  isElementScrollbarVisible,
  PriceStatus,
} from '@toggle/helpers';
import {
  ColumnsTypes,
  StatusTypes,
  statusTypes,
  TableData,
  TableRow,
  TableV2Payload,
} from '@toggle/toggle';
import React, {
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icon, IconProps } from '~/components/icon/Icon';

import { Tooltip } from '../../tooltip/Tooltip';
import { PinConfig } from '../table-cell/TableCell';
import { WithNAFallback } from '../table-cell/WithNAFallback';
import { formatDecimalPlaces } from '../utils';
import * as S from './TableV2.styles';
import { TableV2Cell } from './TableV2Cell';
import { TableV2Header } from './TableV2Header';

const MOBILE_MAX_VISIBLE_ROWS = 5;
const MOBILE_ROW_HEIGHT = 40;

const isStatusTypes = (value: unknown): value is StatusTypes => {
  return typeof value === 'string' && statusTypes.includes(value);
};

type PickedIconProps = Pick<IconProps, 'iconName' | 'fillColor'>;
const styleMapping: Record<StatusTypes, PickedIconProps> = {
  Success: {
    iconName: 'CheckmarkCircle',
    fillColor: 'var(--viz-bullish)',
  },
  Failed: {
    iconName: 'CloseCircle',
    fillColor: 'var(--viz-bearish)',
  },
};

const leftAlignedColumns = ['text', 'status'];
export const getCellAlignment = (type: ColumnsTypes) =>
  leftAlignedColumns.includes(type) ? 'left' : 'right';

export type PickedTablePayload = Pick<
  TableV2Payload,
  'columnsMeta' | 'data' | 'stickyHeader'
>;

export type TableV2Props = PickedTablePayload & {
  className?: string;
  customHeader?: ReactNode;
  floatingExpandBtn?: ReactNode;
  showBorder?: boolean;
  sliceIndex?: number;
  isTouchDevice?: boolean;
  onRowClick?: (row: Row<TableRow>) => void;
};

export const TableV2 = ({
  className,
  columnsMeta,
  data,
  stickyHeader = false,
  showBorder = true,
  customHeader,
  sliceIndex,
  floatingExpandBtn,
  isTouchDevice = false,
  onRowClick,
}: TableV2Props) => {
  const containerRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setTableWidth(containerRef.current.scrollWidth);
    }
  }, []);

  const columns: ColumnDef<TableRow>[] = columnsMeta.map(item => ({
    id: item.id,
    header: item.title,
    accessorFn: row => row[item.id],
    cell: ({ getValue }) => {
      const value = getValue() as TableData;
      const priceStatus =
        item.type === 'directional'
          ? getPriceStatus(+value.value)
          : PriceStatus.Default;
      const columnData = value.value;
      const isStatusType = item.type === 'status' && isStatusTypes(columnData);

      const getColumnData = () => {
        if (item.type === 'number') {
          return formatDecimalPlaces(columnData);
        }

        return columnData;
      };

      return (
        <Tooltip disabled={!value.tooltip} inPortal label={value.tooltip}>
          <S.CellInner aria-haspopup={!!value.tooltip}>
            {value.logo && <img src={value.logo} alt="logo" />}
            <WithNAFallback
              value={
                columnData && (
                  <S.CellWrapper
                    $priceStatus={priceStatus}
                    $dataStatus={isStatusType ? columnData : undefined}
                  >
                    {isStatusType && (
                      <Icon
                        iconName={styleMapping[columnData].iconName}
                        size={9}
                        fillColor={styleMapping[columnData].fillColor}
                      />
                    )}
                    {getColumnData()}
                  </S.CellWrapper>
                )
              }
            />
          </S.CellInner>
        </Tooltip>
      );
    },
    meta: item,
  }));

  const hasHorizontalScroll = isElementScrollbarVisible(containerRef);
  const pinnedColumns = columnsMeta.filter(p => p.sticky).map(p => p.id);

  const getIsPinned = (column: Column<TableRow>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const getPinConfig = (column: Column<TableRow>): PinConfig | undefined =>
    getIsPinned(column)
      ? {
          left: column.getStart('left'),
          right: column.getStart('right'),
          isLastPinned: column.getIsLastColumn('left'),
        }
      : undefined;

  const slicedData = useMemo(() => {
    return sliceIndex !== undefined ? data.slice(0, sliceIndex) : data;
  }, [sliceIndex, data]);

  const tableMaxHeight = useMemo(() => {
    if (slicedData.length > 5 && isTouchDevice) {
      return MOBILE_MAX_VISIBLE_ROWS * MOBILE_ROW_HEIGHT;
    }

    return undefined;
  }, [slicedData.length, isTouchDevice]);

  const table = useReactTable({
    data: slicedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: pinnedColumns,
      },
    },
  });

  const { rows } = table.getRowModel();

  return (
    <S.Container className={className}>
      <div>{customHeader}</div>
      <S.Wrapper $showBorder={showBorder} $maxHeight={tableMaxHeight}>
        <S.StyledTanStackTableRoot
          $isCustomHeader={!!customHeader}
          ref={containerRef}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableV2Header
                key={headerGroup.id}
                getPinConfig={getPinConfig}
                headerGroup={headerGroup}
                stickyHeader={!!stickyHeader}
              />
            ))}
          </thead>
          <tbody>
            {rows.map(row => (
              <TableV2Cell key={row.id} row={row} onRowClick={onRowClick} />
            ))}
          </tbody>
        </S.StyledTanStackTableRoot>
        {sliceIndex !== undefined && data.length > sliceIndex && (
          <>
            <S.GradientOverlay $width={tableWidth} />
            <S.ButtonWrapper>{floatingExpandBtn}</S.ButtonWrapper>
          </>
        )}
      </S.Wrapper>
    </S.Container>
  );
};

TableV2.Styled = S;
